import React from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';
import { FormattedMessage } from 'react-intl';
import Heading from '@starbucks-web/pattern-library/lib/components/heading';
import Button from '@starbucks-web/pattern-library/lib/components/button';

import BentoCompartment from './bento-compartment';
import styles from './styles.cssm';
import { STATUS_LEVELS } from '../shared/constants';
import messages from './messages';

/**
 * Main Bento Box component - flexible grid for status benefits
 * @returns {JSX.Element|null} The bento box component or null if no compartments
 */
const BentoBox = ({
  compartments = [],
  rewardsLevel = STATUS_LEVELS.GREEN,
  title,
  containerClasses,
  className,
  ...otherProps
}) => {
  if (!compartments.length) {
    return null;
  }

  const containerClassesFinal = cs(
    styles.bentoBox,
    containerClasses,
    className
  );

  const getGridAreaName = (index) => `area-${index + 1}`;

  const getGridClass = () => {
    const count = compartments.length;
    return `${styles.bentoGrid} ${styles[`grid-${count}`] || styles.gridAuto}`;
  };

  return (
    <section
      aria-labelledby={title ? `bento-title-${rewardsLevel}` : undefined}
      className={containerClassesFinal}
      {...otherProps}
    >
      {title && (
        <Heading
          className="text-center text-bold mb4 color-greenOnPrimary"
          id={`bento-title-${rewardsLevel}`}
          size="lg"
          tagName="h2"
        >
          {title}
        </Heading>
      )}

      <div className={getGridClass()}>
        {compartments.map((compartment, index) => (
          <div
            className={styles.bentoCompartment}
            key={`compartment-${index}`}
            style={{ gridArea: getGridAreaName(index) }}
          >
            <BentoCompartment {...compartment} rewardsLevel={rewardsLevel} />
          </div>
        ))}
      </div>

      <div className="text-center mt5">
        <div
          className={cs(
            styles.bottomText,
            'mb3 text-xs text-semibold md-text-sm color-greenOnPrimary'
          )}
        >
          <FormattedMessage {...messages.seeHowBenefitsGrow} />
        </div>
        <Button visualStyle="default">
          <FormattedMessage {...messages.compareStatusLevels} />
        </Button>
      </div>
    </section>
  );
};

BentoBox.propTypes = {
  className: PropTypes.string,
  compartments: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      heading: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
        .isRequired,
      body: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      cta: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
    })
  ).isRequired,
  containerClasses: PropTypes.string,
  rewardsLevel: PropTypes.oneOf([
    STATUS_LEVELS.GREEN,
    STATUS_LEVELS.GOLD,
    STATUS_LEVELS.RESERVE,
  ]),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

export default BentoBox;
